import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Maximize2 } from "lucide-react";
import type { AIInsight, EventTool, ToolAccent } from "../lib/types";
import { useInsightTickerSlots, type TickerSlot } from "../hooks/useInsightTickerSlots";

interface Props {
  insights: AIInsight[];
  tools: EventTool[];
  breakingToolId: string | null;
  onBreakingConsumed: () => void;
  /** Fixed left segment inside the ticker strip (the stage AI anchor). */
  leading?: React.ReactNode;
  /** When provided (and at least one slot exists), the strip becomes
   * clickable and shows an expand affordance — opens the detail overlay. */
  onExpand?: () => void;
}

// How long each slot stays static at the "showcase" position before the
// ticker scrolls to the next slot. Tuned to be long enough to read a short
// insight statically without forcing the room to read while it scrolls.
const DWELL_MS = 2500;

// Extra dwell time when the featured slot is the current BREAKING one — the
// room needs the extra beat to register the chip and re-read the bolder text.
const BREAKING_DWELL_MS = 5000;

// Scroll speed between slots, in CSS pixels per second. Slower than a typical
// stock ticker because the body text is German and dense. Reading happens
// mostly during dwell; scroll just transitions between slots.
const SCROLL_SPEED_PX_PER_SEC = 70;

// Padding from the left edge of the ticker container to where the featured
// slot's leading edge (dot + label) sits during dwell.
const LEADING_PADDING_PX = 48;

// Crossfade interval for the reduced-motion fallback.
const REDUCED_MOTION_INTERVAL_MS = 6000;

const ACCENT_TEXT: Record<ToolAccent, string> = {
  berry: "text-rc-berry",
  lime: "text-rc-lime",
  paper: "text-rc-paper",
  mist: "text-rc-mist",
};

const ACCENT_DOT: Record<ToolAccent, string> = {
  berry: "bg-rc-berry",
  lime: "bg-rc-lime",
  paper: "bg-rc-paper",
  mist: "bg-rc-mist",
};

const EMPTY_STATE: { label: string; body: string; accent: ToolAccent } = {
  label: "REASONZ",
  body: "KI hört zu …",
  accent: "lime",
};

export default function InsightTicker({
  insights,
  tools,
  breakingToolId,
  onBreakingConsumed,
  leading,
  onExpand,
}: Props) {
  const slots = useInsightTickerSlots(insights, tools);
  const prefersReducedMotion = usePrefersReducedMotion();
  const expandHandler = onExpand && slots.length > 0 ? onExpand : undefined;

  if (prefersReducedMotion) {
    return (
      <ReducedMotionTicker
        slots={slots}
        breakingToolId={breakingToolId}
        onBreakingConsumed={onBreakingConsumed}
        leading={leading}
        onExpand={expandHandler}
      />
    );
  }

  return (
    <DwellMarqueeTicker
      slots={slots}
      breakingToolId={breakingToolId}
      onBreakingConsumed={onBreakingConsumed}
      leading={leading}
      onExpand={expandHandler}
    />
  );
}

// ─── Dwell + scroll state machine ────────────────────────────────────────

/**
 * News-ticker style display: each tool's slot dwells at the leading edge for
 * DWELL_MS, then scrolls leftward at constant speed until the next slot's
 * leading edge takes the position. Repeats. Slots are tripled inline so the
 * loop wrap and BREAKING jumps don't expose the end of the track.
 *
 * BREAKING handling:
 * - If breakingToolId is set on a slot that's NOT currently featured, the
 *   dwell ends with a snap (instant featured swap) to that slot. The snap is
 *   intentional — BREAKING is meant to grab attention.
 * - If breakingToolId is on the currently featured slot, dwell uses the
 *   longer BREAKING_DWELL_MS, then the normal scroll-to-next runs and the
 *   flag is consumed.
 * - The BREAKING styling (chip + bolder text) updates instantly whenever
 *   breakingToolId changes, regardless of phase.
 */
function DwellMarqueeTicker({
  slots,
  breakingToolId,
  onBreakingConsumed,
  leading,
  onExpand,
}: {
  slots: TickerSlot[];
  breakingToolId: string | null;
  onBreakingConsumed: () => void;
  leading?: React.ReactNode;
  onExpand?: () => void;
}) {
  const originalListRef = useRef<HTMLUListElement>(null);
  const [slotWidths, setSlotWidths] = useState<number[]>([]);
  const [featured, setFeatured] = useState(0);
  const [phase, setPhase] = useState<"dwell" | "scroll">("dwell");

  // Stable identity key — featured/phase only reset when the SET of tools
  // changes, not when a single tool's insight refreshes via realtime.
  const slotKey = slots.map((s) => s.tool.id).join("|");
  const n = slots.length;

  // Fresh-read refs so the dwell/scroll effects don't have to re-run (and
  // restart their timers) every time breakingToolId or slots flips identity
  // mid-dwell. Timer callbacks read these live at fire time.
  const slotsRef = useRef<TickerSlot[]>(slots);
  const breakingToolIdRef = useRef<string | null>(breakingToolId);
  slotsRef.current = slots;
  breakingToolIdRef.current = breakingToolId;

  // Reset position when the set of tools changes.
  useEffect(() => {
    setFeatured(0);
    setPhase("dwell");
  }, [slotKey]);

  // Measure widths of the FIRST copy's <li> elements. Triple-copy below uses
  // the same widths for offset math.
  useLayoutEffect(() => {
    const list = originalListRef.current;
    if (!list || n === 0) return;
    const measure = () => {
      const els = Array.from(list.querySelectorAll<HTMLLIElement>(":scope > li"));
      setSlotWidths(els.map((el) => el.getBoundingClientRect().width));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(list);
    Array.from(list.querySelectorAll<HTMLLIElement>(":scope > li")).forEach((el) =>
      ro.observe(el),
    );
    return () => ro.disconnect();
  }, [slotKey, n]);

  // Dwell phase — hold for DWELL_MS (or BREAKING_DWELL_MS) then decide.
  useEffect(() => {
    if (phase !== "dwell" || n === 0 || slotWidths.length === 0) return;
    const wasBreakingFeaturedAtStart =
      breakingToolIdRef.current === slotsRef.current[featured]?.tool.id;
    const dwellMs = wasBreakingFeaturedAtStart ? BREAKING_DWELL_MS : DWELL_MS;

    const t = window.setTimeout(() => {
      const live = breakingToolIdRef.current;
      const liveSlots = slotsRef.current;
      const isBreakingFeatured = live === liveSlots[featured]?.tool.id;
      const breakingIdx = live ? liveSlots.findIndex((s) => s.tool.id === live) : -1;

      if (isBreakingFeatured) {
        // Showcase complete — consume and animate to the next slot.
        onBreakingConsumed();
        setPhase("scroll");
      } else if (breakingIdx >= 0 && breakingIdx !== featured) {
        // BREAKING arrived on a different slot — snap to it without an
        // animated scroll. The snap IS the attention-grabber.
        setFeatured(breakingIdx);
      } else {
        // No breaking work to do — animate to the next adjacent slot.
        setPhase("scroll");
      }
    }, dwellMs);
    return () => window.clearTimeout(t);
  }, [phase, featured, n, slotWidths.length, onBreakingConsumed]);

  // Scroll phase — animate transform for slotWidth/speed, then advance.
  useEffect(() => {
    if (phase !== "scroll" || n === 0 || slotWidths.length === 0) return;
    const currentWidth = slotWidths[featured];
    if (!currentWidth) return;
    const scrollMs = (currentWidth / SCROLL_SPEED_PX_PER_SEC) * 1000;
    const t = window.setTimeout(() => {
      setFeatured((f) => (f + 1) % slotsRef.current.length);
      setPhase("dwell");
    }, scrollMs);
    return () => window.clearTimeout(t);
  }, [phase, featured, n, slotWidths]);

  // Empty state — static placeholder, no state machine engagement. With a
  // leading anchor the idle message lives there, so the track stays empty.
  if (n === 0) {
    return (
      <TickerShell leading={leading} onExpand={onExpand}>
        {!leading && (
          <div
            className="flex items-center h-full"
            style={{ paddingLeft: LEADING_PADDING_PX }}
          >
            <EmptyStateSlot />
          </div>
        )}
      </TickerShell>
    );
  }

  const cumulative = slotWidths.slice(0, featured).reduce((a, b) => a + b, 0);
  const baseOffset = LEADING_PADDING_PX - cumulative;
  const targetOffset =
    phase === "scroll" ? baseOffset - (slotWidths[featured] ?? 0) : baseOffset;
  const transitionMs =
    phase === "scroll"
      ? ((slotWidths[featured] ?? 0) / SCROLL_SPEED_PX_PER_SEC) * 1000
      : 0;

  return (
    <TickerShell leading={leading} onExpand={onExpand}>
      <div
        className="flex w-max h-full items-center will-change-transform"
        style={{
          transform: `translateX(${targetOffset}px)`,
          transition: `transform ${transitionMs}ms linear`,
        }}
      >
        {/* Original copy — measured. The triple-copy below covers wrap +
            BREAKING-jump cases so neither exposes the end of the track. */}
        <ul ref={originalListRef} className="inline-flex items-center whitespace-nowrap">
          {slots.map((slot) => (
            <SlotContent
              key={`a-${slot.tool.id}`}
              slot={slot}
              isBreaking={breakingToolId === slot.tool.id}
            />
          ))}
        </ul>
        <ul aria-hidden className="inline-flex items-center whitespace-nowrap">
          {slots.map((slot) => (
            <SlotContent
              key={`b-${slot.tool.id}`}
              slot={slot}
              isBreaking={breakingToolId === slot.tool.id}
            />
          ))}
        </ul>
        <ul aria-hidden className="inline-flex items-center whitespace-nowrap">
          {slots.map((slot) => (
            <SlotContent
              key={`c-${slot.tool.id}`}
              slot={slot}
              isBreaking={breakingToolId === slot.tool.id}
            />
          ))}
        </ul>
      </div>
    </TickerShell>
  );
}

// ─── Reduced-motion variant ───────────────────────────────────────────────

function ReducedMotionTicker({
  slots,
  breakingToolId,
  onBreakingConsumed,
  leading,
  onExpand,
}: {
  slots: TickerSlot[];
  breakingToolId: string | null;
  onBreakingConsumed: () => void;
  leading?: React.ReactNode;
  onExpand?: () => void;
}) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (slots.length <= 1) return;
    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % slots.length);
    }, REDUCED_MOTION_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [slots.length]);

  useEffect(() => {
    if (!breakingToolId) return;
    const id = window.setTimeout(onBreakingConsumed, REDUCED_MOTION_INTERVAL_MS);
    return () => window.clearTimeout(id);
  }, [breakingToolId, onBreakingConsumed]);

  let displayIdx = idx;
  if (breakingToolId) {
    const found = slots.findIndex((s) => s.tool.id === breakingToolId);
    if (found >= 0) displayIdx = found;
  }

  const current = slots[displayIdx] ?? null;
  return (
    <TickerShell leading={leading} onExpand={onExpand}>
      <div className="flex w-full items-center justify-center px-12 h-full">
        {current ? (
          <SlotContent
            slot={current}
            isBreaking={breakingToolId === current.tool.id}
          />
        ) : leading ? null : (
          <EmptyStateSlot />
        )}
      </div>
    </TickerShell>
  );
}

// ─── Shared shell ─────────────────────────────────────────────────────────

function TickerShell({
  children,
  leading,
  onExpand,
}: {
  children: React.ReactNode;
  leading?: React.ReactNode;
  onExpand?: () => void;
}) {
  return (
    <div
      className="flex-shrink-0 h-[72px] border-t border-rc-paper/10 bg-rc-graphite/60 overflow-hidden font-mono flex items-stretch"
      role="status"
      aria-live="polite"
    >
      {leading && (
        <div className="flex-shrink-0 flex items-center border-r border-rc-paper/10 px-6">
          {leading}
        </div>
      )}
      {/* The expand click lives on the track container only — clicks in the
          leading anchor segment (bot menu) must not open the overlay. */}
      <div
        className={`relative flex-1 min-w-0 h-full overflow-hidden ${
          onExpand ? "cursor-pointer group/ticker" : ""
        }`}
        onClick={onExpand}
        role={onExpand ? "button" : undefined}
        aria-label={onExpand ? "Insights im Detail anzeigen" : undefined}
      >
        {children}
        {onExpand && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-rc-paper/25 group-hover/ticker:text-rc-paper/70 transition-colors pointer-events-none">
            <Maximize2 className="w-4 h-4" />
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Slot rendering ──────────────────────────────────────────────────────

interface SlotContentProps {
  slot: TickerSlot;
  isBreaking: boolean;
}

function SlotContent({ slot, isBreaking }: SlotContentProps) {
  const accent = slot.tool.accent;
  const dotCls = ACCENT_DOT[accent] ?? ACCENT_DOT.paper;
  const labelCls = ACCENT_TEXT[accent] ?? ACCENT_TEXT.paper;
  const bodyCls = isBreaking ? "text-rc-paper font-semibold" : "text-rc-paper/85";
  const fontSize = "clamp(14px, 1vw, 16px)";

  return (
    <li className="inline-flex items-center gap-3 pr-12" style={{ fontSize }}>
      {isBreaking && <BreakingChip />}
      <span className={`inline-block w-2 h-2 rounded-full ${dotCls} flex-shrink-0`} />
      <span
        className={`font-semibold tracking-[0.22em] uppercase ${labelCls}`}
        style={{ fontSize: "0.78em" }}
      >
        {slot.tool.label}
      </span>
      <span className="text-rc-paper/30">·</span>
      <span className={bodyCls}>{slot.insight.content}</span>
    </li>
  );
}

function EmptyStateSlot() {
  const fontSize = "clamp(14px, 1vw, 16px)";
  return (
    <span className="inline-flex items-center gap-3" style={{ fontSize }}>
      <span
        className={`inline-block w-2 h-2 rounded-full ${ACCENT_DOT[EMPTY_STATE.accent]} flex-shrink-0`}
      />
      <span
        className={`font-semibold tracking-[0.22em] uppercase ${ACCENT_TEXT[EMPTY_STATE.accent]}`}
        style={{ fontSize: "0.78em" }}
      >
        {EMPTY_STATE.label}
      </span>
      <span className="text-rc-paper/30">·</span>
      <span className="text-rc-paper/70 italic">{EMPTY_STATE.body}</span>
    </span>
  );
}

function BreakingChip() {
  return (
    <span className="inline-flex items-center gap-1.5 bg-rc-berry text-rc-paper px-2 py-0.5 rounded-pill text-[10px] font-bold tracking-[0.18em] uppercase animate-pulse flex-shrink-0">
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-rc-paper" />
      Breaking
    </span>
  );
}

// ─── prefers-reduced-motion hook ──────────────────────────────────────────

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return reduced;
}
